import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ConfirmDeleteModal from '../components/common/ConfirmDeleteModal/ConfirmDeleteModal';
import {
  CheckCircle,
  Search, Edit2, Trash2, ChevronLeft, ChevronRight, ChevronDown
} from 'lucide-react';
import InnerHeader from '../components/common/InnerHeader/InnerHeader';
import InnerFooter from '../components/common/InnerFooter/InnerFooter';
import AssetsSidebar from '../components/common/AssetsSidebar/AssetsSidebar';
import Autocomplete from '../components/common/Autocomplete/Autocomplete';
import PageTitleBar from '../components/common/PageTitleBar/PageTitleBar';
import locationService from '../features/auth/services/locationService';
import './Dashboard.css';
import './Sites.css';
import './styles/pages-common.css';

// ─── Initial Data ─────────────────────────────────────────────────────────────
const INITIAL_SITES = [
  { id: 1, name: 'Assembly', description: '', address: '', apt: '', city: 'Delhi', state: 'Delhi', zip: '110054', country: 'India' },
  { id: 2, name: 'Connaught Place', description: '', address: '', apt: '', city: 'Delhi', state: 'Delhi', zip: '110001', country: 'India' },
  { id: 3, name: 'Hussai Sagar Lake', description: '', address: '', apt: '', city: 'Hyderabad', state: 'Telangana', zip: '500033', country: 'India' },
  { id: 4, name: 'Assembly', description: '', address: '', apt: '', city: 'Delhi', state: 'Delhi', zip: '110054', country: 'India' },
  { id: 5, name: 'Connaught Place', description: '', address: '', apt: '', city: 'Delhi', state: 'Delhi', zip: '110001', country: 'India' },
  { id: 6, name: 'Hussai Sagar Lake', description: '', address: '', apt: '', city: 'Hyderabad', state: 'Telangana', zip: '500033', country: 'India' },
  { id: 7, name: 'Assembly', description: '', address: '', apt: '', city: 'Delhi', state: 'Delhi', zip: '110054', country: 'India' },
  { id: 8, name: 'Connaught Place', description: '', address: '', apt: '', city: 'Delhi', state: 'Delhi', zip: '110001', country: 'India' },
  { id: 9, name: 'Hussai Sagar Lake', description: '', address: '', apt: '', city: 'Hyderabad', state: 'Telangana', zip: '500033', country: 'India' },
  { id: 10, name: 'Assembly', description: '', address: '', apt: '', city: 'Delhi', state: 'Delhi', zip: '110054', country: 'India' },
  { id: 11, name: 'Connaught Place', description: '', address: '', apt: '', city: 'Delhi', state: 'Delhi', zip: '110001', country: 'India' },
  { id: 12, name: 'Hussai Sagar Lake', description: '', address: '', apt: '', city: 'Hyderabad', state: 'Telangana', zip: '500033', country: 'India' },
];

const ROWS_PER_PAGE = 10;

const TABLE_COLS = [
  { key: 'name', label: 'Site', sortable: true },
  { key: 'description', label: 'Description', sortable: false },
  { key: 'address', label: 'Address', sortable: false },
  { key: 'apt', label: 'Apt. / Suite', sortable: false },
  { key: 'city', label: 'City', sortable: true },
  { key: 'state', label: 'State', sortable: true },
  { key: 'zip', label: 'Zip', sortable: false },
  { key: 'country', label: 'Country', sortable: false },
  { key: 'action', label: 'Action', sortable: false },
];

const EMPTY_FORM = {
  site: '', description: '', address: '', apt: '',
  city: null, state: null, postalCode: '', country: null,
};

// ─── Component ────────────────────────────────────────────────────────────────
export const Sites = ({ setView }) => {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem('user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [alert, setAlert] = useState(null);
  const [sites, setSites] = useState(null); // mutable list
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);

  // ── Form state ───────────────────────────────────────────────────────────────
  // showForm: false = table view, true = add/edit form
  // editingSite: null = Add mode, site object = Edit mode
  const [showForm, setShowForm] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [pendingDelete, setPendingDelete] = useState(null);

  // ── Location state ───────────────────────────────────────────────────────────
  const [countriesData, setCountriesData] = useState([]);
  const [statesData, setStatesData] = useState([]);
  const [citiesData, setCitiesData] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [statesLoading, setStatesLoading] = useState(false);
  const [citiesLoading, setCitiesLoading] = useState(false);

  // ── Fetch user ──────────────────────────────────────────────────────────────
  const fetchUserDetails = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) return null;
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user/AlluserDetails/`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('user');
          return null;
        }
        throw new Error('Failed');
      }
      const data = await response.json();
      if (data.status && data.data?.user) {
        const userData = data.data.user;
        if (userData.profile_picture && !userData.picture) userData.picture = userData.profile_picture;
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        return userData;
      }
      throw new Error('Invalid data');
    } catch {
      const stored = localStorage.getItem('user');
      if (stored) { const u = JSON.parse(stored); setUser(u); return u; }
      return null;
    }
  };

  const fetchSites = async (isMounted = true) => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) return;
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/sites/`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('accessToken');
          if (setView) setView('signin');
          return;
        }
        throw new Error('Failed to fetch sites');
      }
      const data = await response.json();
      if (data && data.status && Array.isArray(data.data)) {
        const siteList = data.data;

        // Render basic sites immediately (unhydrated state/city/country IDs)
        const basicSites = siteList.map(s => ({
          id: s.id || s.site_id,
          name: s.site_name || '',
          description: s.site_desc || '',
          address: s.site_address || '',
          apt: s.site_apt_suite || '',
          city: s.city_name || s.site_city || '',
          state: s.state_name || s.site_state || '',
          zip: s.postal_code || '',
          country: s.country_name || s.site_country || '',
          cityObj: { id: s.site_city, name: s.city_name || '' },
          stateObj: { id: s.site_state, name: s.state_name || '' },
          countryObj: { id: s.site_country, name: s.country_name || '' }
        }));
        if (isMounted) {
          setSites(basicSites);
        }

        // Trigger heavy hydration in the background
        (async () => {
          try {
            // Fetch all countries
            const countries = await locationService.getCountries();

            // Cache for states and cities to avoid redundant API calls
            const stateCache = {};
            const cityCache = {};

            const hydratedSites = await Promise.all(siteList.map(async (s) => {
               let countryName = s.country_name || '';
               let stateName = s.state_name || '';
               let cityName = s.city_name || '';

               const countryId = s.site_country;
               const stateId = s.site_state;
               const cityId = s.site_city;

               // Resolve Country
               if (countryId && !countryName) {
                 const c = countries.find(x => String(x.id) === String(countryId));
                 if (c) countryName = c.name;
               }

               // Resolve State
               if (countryId && stateId && !stateName) {
                  if (!stateCache[countryId]) {
                     try {
                        stateCache[countryId] = await locationService.getStates(countryId);
                     } catch (e) {
                        stateCache[countryId] = [];
                     }
                  }
                  const st = stateCache[countryId].find(x => String(x.id) === String(stateId));
                  if (st) stateName = st.name;
               }

               // Resolve City
               if (countryId && stateId && cityId && !cityName) {
                  const cacheKey = `${countryId}_${stateId}`;
                  if (!cityCache[cacheKey]) {
                     try {
                        cityCache[cacheKey] = await locationService.getCities(countryId, stateId);
                     } catch (e) {
                        cityCache[cacheKey] = [];
                     }
                  }
                  const cy = cityCache[cacheKey].find(x => String(x.id) === String(cityId));
                  if (cy) cityName = cy.name;
               }

               return {
                 id: s.id || s.site_id,
                 name: s.site_name || '',
                 description: s.site_desc || '',
                 address: s.site_address || '',
                 apt: s.site_apt_suite || '',
                 city: cityName || cityId || '',
                 state: stateName || stateId || '',
                 zip: s.postal_code || '',
                 country: countryName || countryId || '',
                 cityObj: { id: cityId, name: cityName },
                 stateObj: { id: stateId, name: stateName },
                 countryObj: { id: countryId, name: countryName }
               };
            }));
            if (isMounted) {
              setSites(hydratedSites);
            }
          } catch (e) {
            console.error("Failed background hydration for sites:", e);
          }
        })();
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    let isMounted = true;

    // Check user details in background
    fetchUserDetails().then(userData => {
      if (!userData && isMounted) {
        setAlert({ type: 'error', message: 'Error loading user information' });
        setTimeout(() => setView && setView('signin'), 500);
      }
    });

    // Fetch sites progressively
    fetchSites(isMounted);

    // Fetch countries autocomplete data in background
    setCountriesLoading(true);
    locationService.getCountries().then(countries => {
      if (isMounted) {
        setCountriesData(countries);
      }
    }).catch(error => {
      console.error('Error fetching countries:', error);
    }).finally(() => {
      if (isMounted) {
        setCountriesLoading(false);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [setView]);

  // ── Table logic ──────────────────────────────────────────────────────────────
  const filtered = (sites || []).filter(s =>
    !searchQuery ||
    s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.state.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sorted = [...filtered].sort((a, b) => {
    const av = (a[sortField] || '').toString();
    const bv = (b[sortField] || '').toString();
    return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  });

  const totalPages = Math.max(1, Math.ceil(sorted.length / ROWS_PER_PAGE));
  const paginated = sorted.slice((currentPage - 1) * ROWS_PER_PAGE, currentPage * ROWS_PER_PAGE);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  // ── Open Add form ────────────────────────────────────────────────────────────
  const openAddForm = () => {
    setEditingSite(null);
    setFormData(EMPTY_FORM);
    setFormError('');
    setShowForm(true);
  };

  // ── Open Edit form (pre-fill with site data) ─────────────────────────────────
  const openEditForm = (site) => {
    setEditingSite(site);
    setFormData({
      site: site.name,
      description: site.description,
      address: site.address,
      apt: site.apt,
      city: site.cityObj,
      state: site.stateObj,
      postalCode: site.zip,
      country: site.countryObj,
    });

    // Fetch states and cities for edit mode
    (async () => {
      try {
        if (site.countryObj?.id) {
          setStatesLoading(true);
          const states = await locationService.getStates(site.countryObj.id);
          setStatesData(states);
          setStatesLoading(false);

          if (site.stateObj?.id) {
            setCitiesLoading(true);
            const cities = await locationService.getCities(site.countryObj.id, site.stateObj.id);
            setCitiesData(cities);
            setCitiesLoading(false);
          }
        }
      } catch (err) {
        console.error(err);
      }
    })();
    setFormError('');
    setShowForm(true);
  };

  // ── Delete a site ────────────────────────────────────────────────────────────
  const handleDelete = () => {
    setSites(prev => prev.filter(s => s.id !== pendingDelete));
    setAlert({ type: 'success', message: 'Site deleted successfully.' });
    setTimeout(() => setAlert(null), 3000);
    setCurrentPage(p => Math.max(1, Math.min(p, Math.ceil((sites.length - 1) / ROWS_PER_PAGE))));
    setPendingDelete(null);
  };

  // ── Form field change ────────────────────────────────────────────────────────
  const handleFormChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setFormError('');
  };

  const handleCountryChange = async (country) => {
    setFormData(prev => ({ ...prev, country, state: null, city: null }));
    if (formError) setFormError('');
    if (!country?.id) {
      setStatesData([]);
      setCitiesData([]);
      return;
    }
    try {
      setStatesLoading(true);
      const states = await locationService.getStates(country.id);
      setStatesData(states);
      setCitiesData([]);
    } catch (error) {
      console.error(error);
    } finally {
      setStatesLoading(false);
    }
  };

  const handleStateChange = async (state) => {
    setFormData(prev => ({ ...prev, state, city: null }));
    if (formError) setFormError('');
    if (!formData.country?.id || !state?.id) {
      setCitiesData([]);
      return;
    }
    try {
      setCitiesLoading(true);
      const cities = await locationService.getCities(formData.country.id, state.id);
      setCitiesData(cities);
    } catch (error) {
      console.error(error);
    } finally {
      setCitiesLoading(false);
    }
  };

  // ── Form submit (Add or Update) ──────────────────────────────────────────────
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.site.trim()) { setFormError('Site name is required.'); return; }
    if (!formData.country?.id) { setFormError('Country is required.'); return; }
    if (!formData.state?.id) { setFormError('State is required.'); return; }
    if (!formData.city?.id) { setFormError('City is required.'); return; }

    const accessToken = localStorage.getItem('accessToken');

    if (editingSite) {
      // ── UPDATE existing site via API ──
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/sites/update/${editingSite.id}/`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            site_name: formData.site,
            site_desc: formData.description,
            site_address: formData.address,
            site_apt_suite: formData.apt,
            site_country: formData.country.id,
            site_state: formData.state.id,
            site_city: formData.city.id,
            postal_code: formData.postalCode,
          }),
        });

        if (!response.ok) {
          if (response.status === 401) {
            localStorage.removeItem('accessToken');
            if (setView) setView('signin');
            return;
          }
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.detail || errData.message || 'Failed to update site');
        }

        await fetchSites();
        setAlert({ type: 'success', message: `Site "${formData.site}" updated successfully!` });
      } catch (error) {
        setFormError(error.message || 'Error updating site');
        return;
      }

    } else {
      // ── ADD new site ──
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/sites/create/`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            site_name: formData.site,
            site_desc: formData.description,
            site_address: formData.address,
            site_apt_suite: formData.apt,
            site_country: formData.country.id,
            site_state: formData.state.id,
            site_city: formData.city.id,
            postal_code: formData.postalCode,
          })
        });

        if (!response.ok) {
          if (response.status === 401) {
            localStorage.removeItem('accessToken');
            if (setView) setView('signin');
            return;
          }
          throw new Error('Failed to create site');
        }

        await fetchSites();
        setAlert({ type: 'success', message: `Site "${formData.site}" added successfully!` });
      } catch (error) {
        setFormError(error.message || 'Error creating site');
        return;
      }
    }

    setTimeout(() => setAlert(null), 3000);
    setFormData(EMPTY_FORM);
    setEditingSite(null);
    setShowForm(false);
  };

  // ── Cancel form ──────────────────────────────────────────────────────────────
  const handleCancel = () => {
    setFormData(EMPTY_FORM);
    setEditingSite(null);
    setFormError('');
    setShowForm(false);
  };



  const isEditMode = Boolean(editingSite);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="assets-root"
    >

      {/* ══ 1. MAIN HEADER ══════════════════════════════════════════════════════ */}
      <InnerHeader
        user={user}
        setView={setView}
        onProfilePhotoSuccess={async () => {
          setTimeout(async () => { await fetchUserDetails(); }, 500);
          setAlert({ type: 'success', message: 'Profile picture updated successfully!' });
          setTimeout(() => setAlert(null), 3000);
        }}
      />

      {/* ══ 2. PAGE TITLE BAR ════════════════════════════════════════════════ */}
      <PageTitleBar title="Sites" setView={setView} activeBtn="sites" />

      {/* ══ 3. BODY ═════════════════════════════════════════════════════════════ */}
      <div className="assets-body">

        {/* Sidebar */}
        <AssetsSidebar setView={setView} activeItem="Sites" />

        {/* Main content */}
        <div className="assets-main">

          {/* Alert */}
          {alert && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`assets-alert ${alert.type}`}
            >
              <CheckCircle size={18} />
              <span className="alert-message-flex">{alert.message}</span>
              <button className="assets-alert-close" onClick={() => setAlert(null)}>×</button>
            </motion.div>
          )}

          {/* ── ADD / EDIT FORM ─────────────────────────────────────────────── */}
          {showForm ? (
            <motion.div
              key="site-form"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="site-form-card"
            >
              <div className="site-form-header">
                <h2 className="site-form-title">
                  {isEditMode ? 'Edit Site' : 'Add New Site'}
                </h2>
                <p className="site-form-subtitle">
                  {isEditMode
                    ? 'Update the site details below.'
                    : 'Enter the data about your new site in the fields below and we will add it to your list.'}
                </p>
              </div>

              <form className="site-form" onSubmit={handleFormSubmit} noValidate>

                {/* Site name */}
                <input
                  className={`site-form-input${formError ? ' error' : ''}`}
                  type="text"
                  name="site"
                  placeholder="Site *"
                  value={formData.site}
                  onChange={handleFormChange}
                />
                {formError && <p className="site-form-error">{formError}</p>}

                {/* Description */}
                <textarea
                  className="site-form-textarea"
                  name="description"
                  placeholder="Description"
                  value={formData.description}
                  onChange={handleFormChange}
                  rows={4}
                />

                {/* Address */}
                <input
                  className="site-form-input"
                  type="text"
                  name="address"
                  placeholder="Address"
                  value={formData.address}
                  onChange={handleFormChange}
                />

                {/* Apt. / Suite */}
                <input
                  className="site-form-input"
                  type="text"
                  name="apt"
                  placeholder="Apt. / Suite"
                  value={formData.apt}
                  onChange={handleFormChange}
                />

                {/* Country + Postal Code */}
                <div className="site-form-row">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Autocomplete
                      options={countriesData}
                      value={formData.country}
                      onChange={handleCountryChange}
                      onInputChange={() => { }}
                      placeholder="Country"
                      getOptionLabel={(opt) => opt.name}
                      getOptionValue={(opt) => opt.id}
                      disabled={countriesLoading}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <input
                      className="site-form-input"
                      type="text"
                      name="postalCode"
                      placeholder="Postal Code"
                      value={formData.postalCode}
                      onChange={handleFormChange}
                    />
                  </div>
                </div>

                {/* State + City */}
                <div className="site-form-row">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Autocomplete
                      options={statesData}
                      value={formData.state}
                      onChange={handleStateChange}
                      onInputChange={() => { }}
                      placeholder="State"
                      getOptionLabel={(opt) => opt.name}
                      getOptionValue={(opt) => opt.id}
                      disabled={statesLoading || !formData.country}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Autocomplete
                      options={citiesData}
                      value={formData.city}
                      onChange={(city) => { setFormData(prev => ({ ...prev, city })); if (formError) setFormError(''); }}
                      onInputChange={() => { }}
                      placeholder="City"
                      getOptionLabel={(opt) => opt.name}
                      getOptionValue={(opt) => opt.id}
                      disabled={citiesLoading || !formData.state}
                    />
                  </div>
                </div>

                {/* Actions */}
                <div className="site-form-actions">
                  <button type="submit" className="site-form-btn-add">
                    {isEditMode ? 'Update' : 'Add'}
                  </button>
                  <button type="button" className="site-form-btn-cancel" onClick={handleCancel}>
                    Cancel
                  </button>
                </div>

              </form>
            </motion.div>

          ) : (

            /* ── SITES TABLE ──────────────────────────────────────────────── */
            <div className="assets-card">
              <div className="assets-card-title">List of Sites</div>

              {/* Toolbar */}
              <div className="assets-toolbar">
                <div className="assets-search-wrap">
                  <Search size={14} color="#64748b" />
                  <input
                    className="assets-search-input"
                    placeholder="Search"
                    value={searchQuery}
                    onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                  />
                </div>
                <button className="assets-add-btn" onClick={openAddForm}>
                  Add New Site
                </button>
              </div>

              {/* Table */}
              <div className="assets-table-wrap">
                <table className="assets-table">
                  <thead>
                    <tr>
                      {TABLE_COLS.map(col => {
                        const isSorted = sortField === col.key && col.sortable;
                        return (
                          <th
                            key={col.key}
                            className={['assets-th', col.sortable ? 'sortable' : '', isSorted ? 'sorted' : ''].join(' ')}
                            onClick={() => col.sortable && handleSort(col.key)}
                          >
                            {col.label}
                            {col.sortable && (
                              <span className={`assets-sort-arrow${isSorted ? ' active' : ''}`}>
                                {isSorted && sortDir === 'desc' ? '↓' : '↑'}
                              </span>
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {sites === null ? (
                      [...Array(5)].map((_, i) => (
                        <tr key={`skeleton-${i}`}>
                          <td colSpan={9} className="assets-td">
                            <div className="skeleton-loader" style={{ height: '24px', margin: '4px 0' }}>
                              <div className="skeleton-bar" style={{ height: '100%', width: '100%' }}></div>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : paginated.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="assets-td assets-table-empty">No sites found.</td>
                      </tr>
                    ) : paginated.map((site, idx) => (
                      <tr
                        key={site.id}
                        className={idx % 2 === 0 ? 'assets-table-row-even' : 'assets-table-row-odd'}
                      >
                        {['name', 'description', 'address', 'apt', 'city', 'state', 'zip', 'country'].map(col => (
                          <td key={col} className="assets-td">{site[col]}</td>
                        ))}
                        <td className="assets-td">
                          <span className="assets-action-group">
                            <button
                              className="assets-action-btn edit"
                              title="Edit"
                              onClick={() => openEditForm(site)}
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              className="assets-action-btn delete"
                              title="Delete"
                              onClick={() => setPendingDelete(site.id)}
                            >
                              <Trash2 size={14} />
                            </button>
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="assets-pagination">
                <button
                  className="assets-page-btn"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                >
                  <ChevronLeft size={14} />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    className={`assets-page-btn${currentPage === page ? ' active' : ''}`}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                ))}
                <button
                  className="assets-page-btn"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* ── Confirm Delete Modal ──────────────────────────────────────────────── */}
      <AnimatePresence>
        {pendingDelete && (
          <ConfirmDeleteModal
            message="Are you sure you want to delete this site? This action cannot be undone."
            onConfirm={handleDelete}
            onCancel={() => setPendingDelete(null)}
          />
        )}
      </AnimatePresence>

      {/* ══ 4. FOOTER ═══════════════════════════════════════════════════════════ */}
      <InnerFooter />

    </motion.div>
  );
};

export default Sites;
